<template>
  <SmTable
    :data="data"
    :columns="computedColumns"
    :loading="loading"
    key-field="id"
    class="h-full"
    @row-click="handleRowClick">
    <template #id="{ row }">
      <TextMultiline
        tooltip-placement="top"
        :lines-to-show="getPersonCellLineCount(row)"
        class="block w-full min-w-0 overflow-hidden">
        <span
          v-if="browserExtensionEmailEnabled && getDisplayEmail(row)"
          class="text-contrast-default block w-full text-sm font-medium">
          {{ getDisplayEmail(row) }}
        </span>
        <span class="font-mono block w-full text-sm">{{ row.id }}</span>
      </TextMultiline>
    </template>

    <template #first_seen="{ row }">
      {{
        getFirstSeen(row) > 0
          ? displayDate(new Date(getFirstSeen(row) * 1000))
          : '-'
      }}
    </template>

    <template #last_seen="{ row }">
      {{
        getLastSeen(row) > 0
          ? displayDate(new Date(getLastSeen(row) * 1000))
          : '-'
      }}
    </template>

    <template #total_sessions="{ row }">
      {{ row.metadata.total_sessions }}
    </template>

    <template #active_days="{ row }">
      {{ row.metadata.active_days }}
    </template>

    <template #no-data>
      <EmptyState
        :icon="Users"
        :title="i18n.t('browserExtension.noUsersFound')"
        :show-create-btn="false"
        :show-docs-btn="false" />
    </template>
  </SmTable>
</template>

<script setup lang="ts">
  import { computed } from 'vue'
  import { useI18n } from 'vue-i18n'
  import { Users } from 'lucide-vue-next'
  import type { Column } from '@/components/sm/SmTable.types'
  import type { UsageStatsWithEmail } from '@/client'
  import { useSessionStore } from '@/stores/sessionStore'
  import { capitalize } from '@/utils/formatter/formatter'
  import { displayDate } from '@/utils/formatter/date'
  import { compareNumbers } from '@/utils/sort'

  interface Props {
    data: UsageStatsWithEmail[]
    loading?: boolean
    compact?: boolean
    visibleColumns?: Array<
      | keyof UsageStatsWithEmail
      | 'first_seen'
      | 'last_seen'
      | 'total_sessions'
      | 'active_days'
    >
  }

  const props = withDefaults(defineProps<Props>(), {
    loading: false,
    compact: false,
    visibleColumns: undefined,
  })

  const emit = defineEmits<{
    'row-click': [iRow: number, row: UsageStatsWithEmail]
  }>()

  // TODO: why do we need this?
  type VisibleUsersColumn =
    | keyof UsageStatsWithEmail
    | 'first_seen'
    | 'last_seen'
    | 'total_sessions'
    | 'active_days'

  const i18n = useI18n()
  const sessionStore = useSessionStore()

  const browserExtensionEmailEnabled = computed(function () {
    return !!sessionStore.currentCompanyInfo?.features.browser_extension_email
  })

  // All available column definitions
  const allColumns = computed<Column<UsageStatsWithEmail>[]>(function () {
    return [
      {
        key: 'id',
        label: capitalize(i18n.t('browserExtension.person', 'Person')),
        width: 2,
        sortable: false,
        loader: true,
      },
      {
        key: 'first_seen',
        label: capitalize(i18n.t('browserExtension.firstSeen')),
        width: 1,
        sortable: true,
        sortFn: function (a, b) {
          return compareNumbers(getFirstSeen(a), getFirstSeen(b))
        },
        loader: true,
      },
      {
        key: 'last_seen',
        label: capitalize(i18n.t('browserExtension.lastSeen')),
        width: 1,
        sortable: true,
        sortFn: function (a, b) {
          return compareNumbers(getLastSeen(a), getLastSeen(b))
        },
        loader: true,
      },
      {
        key: 'total_sessions',
        label: capitalize(i18n.t('browserExtension.totalSessions')),
        width: 1,
        sortable: true,
        sortFn: function (a, b) {
          return compareNumbers(
            Number(a.metadata?.total_sessions || 0),
            Number(b.metadata?.total_sessions || 0)
          )
        },
        loader: true,
      },
      {
        key: 'active_days',
        label: capitalize(i18n.t('browserExtension.activeDays')),
        width: 1,
        sortable: true,
        sortFn: function (a, b) {
          return compareNumbers(
            Number(a.metadata?.active_days || 0),
            Number(b.metadata?.active_days || 0)
          )
        },
        loader: true,
      },
    ]
  })

  // Filter columns based on visibleColumns prop
  const computedColumns = computed<Column<UsageStatsWithEmail>[]>(() => {
    if (!props.visibleColumns || props.visibleColumns.length === 0) {
      // In compact mode, show fewer columns
      if (props.compact) {
        return allColumns.value.filter((col) =>
          ['id', 'last_seen', 'total_sessions'].includes(col.key)
        )
      }
      return allColumns.value
    }

    return allColumns.value.filter((col) => {
      const columnKey = col.key as VisibleUsersColumn
      return props.visibleColumns?.includes(columnKey)
    })
  })

  function getDisplayEmail(item: UsageStatsWithEmail): string | null {
    if (!item.email) {
      return null
    }

    const normalizedEmail = item.email.trim()
    if (!normalizedEmail) {
      return null
    }

    return normalizedEmail
  }

  function getFirstSeen(item: UsageStatsWithEmail): number {
    return item.daily_usages?.[0]?.timestamp || 0
  }

  function getPersonCellLineCount(item: UsageStatsWithEmail): number {
    return browserExtensionEmailEnabled.value && getDisplayEmail(item) ? 2 : 1
  }

  function getLastSeen(item: UsageStatsWithEmail): number {
    if (!item.daily_usages || item.daily_usages.length === 0) return 0
    return item.daily_usages[item.daily_usages.length - 1]?.timestamp || 0
  }

  function handleRowClick(iRow: number, row: UsageStatsWithEmail) {
    emit('row-click', iRow, row)
  }

  // Expose helper functions for external use if needed
  defineExpose({
    getFirstSeen,
    getLastSeen,
  })
</script>
